/**
 * HomeCTABlock
 * ------------
 * Full-width black manifesto CTA rendered between AphorismChat and the
 * Footer on the Home page. The entire block is a `mailto:` link — a
 * single, high-impact conversion touchpoint.
 *
 * Data flow:
 *   - Reads the contact email from `/api/settings/contacts` so the
 *     Admin CMS remains the source of truth. Falls back to a build-time
 *     default and logs a warning if the API returns an empty email.
 *
 * Motion:
 *   - IntersectionObserver (threshold 0.3) toggles `is-in` on mount
 *     into view. CSS drives the staggered fade+translateY on the two
 *     display lines and the email caption.
 *   - Skipped entirely under `prefers-reduced-motion: reduce`; the
 *     block just becomes visible without motion.
 */
import React, { useEffect, useRef, useState } from 'react';
import { API } from '../../lib/api';
import './home-cta-block.css';

const FALLBACK_EMAIL = 'paolo@alldesignsrl.com';
const SUBJECT = 'Progetto — via araialldesign.com';

export default function HomeCTABlock() {
    const [email, setEmail] = useState(FALLBACK_EMAIL);
    const ref = useRef(null);

    useEffect(() => {
        fetch(`${API}/settings/contacts`)
            .then((r) => r.json())
            .then((d) => {
                const e = (d && d.email) ? String(d.email).trim() : '';
                if (!e) {
                    console.warn('[HomeCTABlock] settings.contacts.email is empty — using fallback');
                    setEmail(FALLBACK_EMAIL);
                } else {
                    setEmail(e);
                }
            })
            .catch(() => setEmail(FALLBACK_EMAIL));
    }, []);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced) { el.classList.add('is-in'); return; }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        el.classList.add('is-in');
                        io.disconnect();
                    }
                });
            },
            { threshold: 0.3 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const href = `mailto:${email}?subject=${encodeURIComponent(SUBJECT)}`;

    return (
        <a
            ref={ref}
            href={href}
            className="home-cta-block"
            aria-label={`Contact ${email}`}
            data-testid="home-cta-block"
            data-analytics="cta-mailto-click"
        >
            <div className="home-cta-block__inner">
                <h2 className="home-cta-block__display" data-testid="home-cta-display">
                    <span className="home-cta-block__line" style={{ '--cta-line': 0 }}>
                        IF YOU WANT ME,
                    </span>
                    <span className="home-cta-block__line" style={{ '--cta-line': 1 }}>
                        FIND ME.
                    </span>
                </h2>
                <p className="home-cta-block__email" data-testid="home-cta-email">
                    <span aria-hidden="true">&rarr;&nbsp;</span>{email}
                </p>
            </div>
        </a>
    );
}
